import React, { useEffect, useRef, useState } from 'react';
import { X, Trash2, ExternalLink, Copy } from 'lucide-react';

const PRESET_COLORS = [
  '', // empty = use theme default
  '#8B5CF6', '#EC4899', '#F59E0B', '#10B981', '#3B82F6',
  '#EF4444', '#0E0E23', '#FFFFFF',
];

/**
 * InlineButtonEditor — small popover anchored to the clicked button.
 * Wired up by `useInlineButtonEditor`. Pages are loaded into a select via
 * the global cache `window.__wb_pages` populated by DashboardContent.
 */
export default function InlineButtonEditor({ state, button, onApply, onRemove, onClose }) {
  const [text, setText] = useState('');
  const [link, setLink] = useState('');
  const [color, setColor] = useState('');

  // Only resync local state when a DIFFERENT button is selected.
  // Resyncing on every parent re-render caused the cursor to jump and
  // characters to disappear as the user typed (each keystroke triggered
  // onApply → parent re-render → new `button` object reference → effect →
  // local state reset to the stale prop value before the new keystroke
  // landed). Iter 161 bug fix.
  const selectionKey = state ? `${state.blockId}::${state.field || 'buttons'}::${state.index}` : null;
  const lastKey = useRef(null);
  useEffect(() => {
    if (!button || !selectionKey) return;
    if (lastKey.current === selectionKey) return;
    lastKey.current = selectionKey;
    setText(button.text || '');
    setLink(button.link || '');
    setColor(button.color || '');
  }, [button, selectionKey]);

  if (!state) return null;
  const { rect } = state;

  // Position popover above the button if there's room, else below.
  const POPOVER_HEIGHT = 260;
  const goAbove = rect.top > POPOVER_HEIGHT + 12;
  const top = goAbove ? rect.top - POPOVER_HEIGHT - 10 : rect.bottom + 10;
  const left = Math.max(12, Math.min(window.innerWidth - 320, rect.left));

  const pages = Array.isArray(window.__wb_pages) ? window.__wb_pages : [];

  return (
    <div
      data-wb-button-editor
      className="fixed z-[260] w-[300px] bg-[#15152E] border border-white/[0.10] rounded-2xl shadow-[0_24px_56px_rgba(0,0,0,0.6)] p-4"
      style={{ top, left }}
      data-testid="inline-button-editor"
    >
      <div className="flex items-center justify-between mb-3">
        <p className="text-[10px] uppercase tracking-[0.18em] text-[#A78BFA] font-bold">Edit Button</p>
        <button onClick={onClose} className="p-1 text-white/55 hover:text-white" data-testid="inline-button-editor-close">
          <X size={14} />
        </button>
      </div>

      <label className="block text-[10px] uppercase tracking-wider text-white/55 font-bold mb-1">Label</label>
      <input
        value={text}
        onChange={(e) => { setText(e.target.value); onApply({ text: e.target.value }); }}
        className="w-full bg-white/[0.04] border border-white/[0.08] rounded-md px-3 py-1.5 text-[13px] text-white focus:outline-none focus:border-[#8B5CF6]/40"
        placeholder="Get Started"
        data-testid="inline-button-text"
      />

      <label className="block text-[10px] uppercase tracking-wider text-white/55 font-bold mb-1 mt-3">Link</label>
      <div className="flex gap-1.5">
        <input
          value={link}
          onChange={(e) => { setLink(e.target.value); onApply({ link: e.target.value }); }}
          className="flex-1 bg-white/[0.04] border border-white/[0.08] rounded-md px-3 py-1.5 text-[13px] text-white focus:outline-none focus:border-[#8B5CF6]/40"
          placeholder="https://… or /page-slug"
          data-testid="inline-button-link"
        />
        {link && /^https?:/.test(link) && (
          <a href={link} target="_blank" rel="noreferrer" className="px-2 py-1.5 text-white/55 hover:text-white" title="Open in new tab">
            <ExternalLink size={13} />
          </a>
        )}
      </div>
      {pages.length > 0 && (
        <select
          value={pages.find(p => link === `/${p.slug || p.id}`)?.id || ''}
          onChange={(e) => {
            const sel = pages.find(p => p.id === e.target.value);
            if (sel) {
              const url = `/${sel.slug || sel.id}`;
              setLink(url);
              onApply({ link: url });
            }
          }}
          className="mt-1.5 w-full bg-white/[0.04] border border-white/[0.08] rounded-md px-3 py-1.5 text-[12px] text-white focus:outline-none"
          data-testid="inline-button-page-picker"
        >
          <option value="">— Link to a page —</option>
          <option value="home">/ (Home)</option>
          {pages.map(p => <option key={p.id} value={p.id}>{p.title}</option>)}
        </select>
      )}

      <label className="block text-[10px] uppercase tracking-wider text-white/55 font-bold mb-1 mt-3">Color</label>
      <div className="flex flex-wrap gap-1.5" data-testid="inline-button-color-grid">
        {PRESET_COLORS.map(c => (
          <button
            key={c || 'theme'}
            onClick={() => { setColor(c); onApply({ color: c }); }}
            className={`w-7 h-7 rounded-full border-2 transition-all ${color === c ? 'border-white scale-110' : 'border-white/15 hover:border-white/40'}`}
            style={c ? { background: c } : {
              background: 'repeating-linear-gradient(45deg, #15152E, #15152E 3px, #A78BFA 3px, #A78BFA 5px)',
            }}
            title={c || 'Theme default'}
            data-testid={`inline-button-color-${c || 'theme'}`}
          />
        ))}
      </div>

      <div className="flex items-center justify-between mt-4 pt-3 border-t border-white/[0.06]">
        <div className="flex items-center gap-1.5">
          <button
            onClick={onRemove}
            className="inline-flex items-center gap-1.5 text-[11px] font-bold text-[#FF6B6B] hover:text-white tracking-wider uppercase"
            data-testid="inline-button-delete"
          >
            <Trash2 size={11} /> Delete
          </button>
          <button
            onClick={() => onApply({ __duplicate: true })}
            className="inline-flex items-center gap-1.5 text-[11px] font-bold text-[#A78BFA] hover:text-white tracking-wider uppercase"
            data-testid="inline-button-duplicate"
            title="Duplicate this button (right next to it)"
          >
            <Copy size={11} /> Duplicate
          </button>
        </div>
        <div className="flex items-center gap-1">
          <button
            onClick={() => onApply({ __move: -1 })}
            className="px-2 py-1.5 rounded-md bg-white/[0.04] hover:bg-white/[0.08] text-white/70 hover:text-white text-[11px] font-bold tracking-wider uppercase border border-white/[0.06]"
            title="Move button left"
            data-testid="inline-button-move-left"
          >
            ←
          </button>
          <button
            onClick={() => onApply({ __move: 1 })}
            className="px-2 py-1.5 rounded-md bg-white/[0.04] hover:bg-white/[0.08] text-white/70 hover:text-white text-[11px] font-bold tracking-wider uppercase border border-white/[0.06]"
            title="Move button right"
            data-testid="inline-button-move-right"
          >
            →
          </button>
          <button
            onClick={onClose}
            className="ml-1 px-3 py-1.5 rounded-md bg-gradient-to-r from-[#8B5CF6] to-[#6D28D9] text-white text-[11px] font-bold uppercase tracking-wider hover:opacity-90"
            data-testid="inline-button-done"
          >
            Done
          </button>
        </div>
      </div>
    </div>
  );
}
